import React, {
  useEffect, useMemo, useState,
} from 'react';

import { Box, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

import ContentCard from '../../components/ContentCard';
import DataGrid from '../../components/DataGrid';
import DateRangePicker from '../../components/DateRangePicker';
import { useGetLessonsQuery } from '../../services/classes';
import { Lesson } from '../../services/classes/types';
import { useGetCoursesQuery } from '../../services/courses';
import { SubjectColorKeys } from '../../theme';
import {
  getLessonCode, getLessonCourse,
  getLessonCourseAndCode,
  getLessonSubject,
} from '../../utils/common/classes';
import { DEFAULT_LOCATION } from '../../utils/common/constants';
import { getColorKeyFromCourse } from '../../utils/common/courses';
import useError from '../../utils/react/hooks/useError';

import { BookletName } from './index.styles';

interface Booklet {
  id: string;
  name: string;
  total: number;
  color: SubjectColorKeys | 'primary';
  [date: string]: number | string;
}

const AdminPrintingPage = () => {
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [booklets, setBooklets] = useState<Booklet[]>([]);

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date);
  };

  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const { data: lessons,
    isLoading: isLessonsLoading,
    isError: isGetLessonsError,
    error: getLessonsError } = useGetLessonsQuery({
    queryStartDatetime: startDate?.startOf('day').valueOf(),
    queryEndDatetime: endDate?.endOf('day').valueOf(),
    location: DEFAULT_LOCATION,
  }, { skip: !startDate || !endDate });

  const generateDateRange = (start: dayjs.Dayjs, end: dayjs.Dayjs): dayjs.Dayjs[] => {
    const dates: dayjs.Dayjs[] = [];
    let currentDate = start.startOf('day');
    const finalDate = end.startOf('day');

    while (currentDate.isSameOrBefore(finalDate)) {
      dates.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }
    return dates;
  };

  useEffect(() => {
    if (startDate && endDate && courses && lessons) {
      const updatedBooklets:Booklet[] = [];

      const dateRange = generateDateRange(startDate, endDate);

      lessons.data.forEach((lesson: Lesson) => {
        const lessonDate = dayjs(lesson.startDatetime).format('DD/MM/YYYY');
        const lessonId = getLessonCourseAndCode(lesson);
        const existingBookletIndex = updatedBooklets.findIndex((item) => item.id === lessonId);
        if (existingBookletIndex !== -1) {
          const currentValue = updatedBooklets[existingBookletIndex][lessonDate];
          updatedBooklets[existingBookletIndex][lessonDate] = (typeof currentValue === 'number' ? currentValue : 0) + (lesson.studentCount || 0);
          updatedBooklets[existingBookletIndex].total += lesson.studentCount || 0;
        } else {
          const lessonSubject = getLessonSubject(lesson);
          const lessonCode = getLessonCode(lesson);
          const lessonLabel = `${lessonSubject.toUpperCase()} ${lessonCode}`;
          const course = getLessonCourse(lesson);
          const color = getColorKeyFromCourse(course, courses.data) ?? 'primary';
          // Initialize the booklet object with fields for each date in the range, initialized to 0
          const newBooklet: Booklet = {
            id: lessonId,
            name: lessonLabel,
            total: 0,
            color,
          };
          dateRange.forEach((date) => {
            newBooklet[date.format('DD/MM/YYYY')] = '-';
          });
          // Update the quantity for the current lesson date
          newBooklet[lessonDate] = lesson.studentCount || 0;

          // Update the total quantity
          newBooklet.total += lesson.studentCount || 0;

          newBooklet[lessonDate] = lesson.studentCount || 0;
          updatedBooklets.push(newBooklet);
        }
      });
      updatedBooklets.sort((a, b) => a.name.localeCompare(b.name));
      setBooklets(updatedBooklets);
    }
  }, [lessons, startDate, endDate, courses]);

  useError(isGetLessonsError, getLessonsError);

  const generateDateColumns = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    const columns = [];
    let currentDate = start;
    while (currentDate.isSameOrBefore(end, 'day')) {
      const column = {
        field: `${currentDate.format('DD/MM/YYYY')}`,
        headerName: currentDate.format('ddd DD/MM'),
        width: 150,
      };
      columns.push(column);
      currentDate = currentDate.add(1, 'day');
    }
    return columns;
  };

  // Memoize the columns
  const columns = useMemo(() => {
    if (!startDate || !endDate) return [];

    const dateColumns = generateDateColumns(startDate, endDate);

    return [
      {
        field: 'name',
        headerName: 'Lesson',
        width: 200,
        renderCell: (params: any) => (
          <BookletName textcolor={params.row.color}>
            {params.value}
          </BookletName>
        ),
      },
      {
        field: 'total',
        headerName: 'Total',
        width: 150,
        renderCell: (params: any) => (
          <Typography variant="subtitle2">
            {params.value}
          </Typography>
        ),
      },
      ...dateColumns,
    ];
  }, [startDate, endDate]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ContentCard
            variant="contained"
            title="Date Range"
          >
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          </ContentCard>
        </Grid>
        <Grid item xs={12}>
          <ContentCard
            variant="outlined"
            title="Booklets"
          >
            {isCoursesLoading || isLessonsLoading ? (
              <div>
                <Typography>
                  Loading...
                </Typography>
              </div>
            ) : (
              <Box display="grid">
                <DataGrid
                  columns={columns}
                  rows={booklets}
                />
              </Box>
            )}
          </ContentCard>
        </Grid>
      </Grid>
    </div>
  );
};
export default AdminPrintingPage;
